import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = { class: "modal-content" }
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["options", "value", "disabled", "required", "isLoading"]
const _hoisted_5 = ["value", "disabled"]
const _hoisted_6 = ["columns"]
const _hoisted_7 = ["options", "value", "disabled", "required", "isLoading"]
const _hoisted_8 = ["value"]
const _hoisted_9 = { key: 0 }
const _hoisted_10 = ["value"]
const _hoisted_11 = ["values", "options", "disabled"]
const _hoisted_12 = ["title"]
const _hoisted_13 = { slot: "footer" }
const _hoisted_14 = ["disabled"]

import { computed, ref, reactive, watchEffect, watch } from 'vue';
import { useStore } from 'vuex';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { objValuesToString } from '../../utils/settings';
import Banner from '@components/Banner/Banner.vue';
import ChartValues from '../settings/ChartValues.vue';
import { useCreateServiceInstance, useBindServiceInstance, useUnbindServiceInstance, useUpdateServiceInstance } from '../../queries/useServiceMutations';
import { ServiceInstance } from '../../models/service/ui-types';
import { useNamespaces } from '../../queries/useNamespaceQueries';
import { useCatalogServices } from '../../queries/useCatalogServicesQueries';
import { useApplications } from '../../queries/useApplicationQueries';
import { debounce } from 'lodash';
import { ListResourceRequestParams, ResourceQueryOptions } from '../../models/resource/ui-types';

import isEqual from 'lodash/isEqual';


export default /*@__PURE__*/_defineComponent({
  __name: 'ServiceInstanceModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');
// Model instance, used only for API calls
const serviceModel = ref<ServiceInstance | null>(null);

// Form fields (separate from the model to avoid proxy mutation issues)
const formNamespace = ref('');
const formName = ref('');
const formCatalogService = ref('');

const initialBoundApps = ref<string[]>([]);
const selectedApps = ref<string[]>([]);
const chartValues = reactive<Record<string, any>>({});
const validChartValues = ref<Record<string, boolean>>({});

const namespaceRequestParams = ref<ListResourceRequestParams>({ page: 1, pageSize: 25, search: '' });
const namespaceRequestOptions = ref<ResourceQueryOptions>({ enabled: false, polling: false });
const {data: namespaces, isLoading: isLoadingNamespaces, isError: isErrorNamespaces, error: namespacesError} = useNamespaces(store, namespaceRequestParams, namespaceRequestOptions);
const catalogServiceRequestParams = ref<ListResourceRequestParams>({ page: 1, pageSize: 25, search: '' });
const catalogServiceRequestOptions = ref<ResourceQueryOptions>({ enabled: false, polling: false });
const {data: catalogServices, isLoading: isLoadingCatalogServices, isError: isErrorCatalogServices, error: catalogServicesError} = useCatalogServices(store, catalogServiceRequestParams, catalogServiceRequestOptions);
const applicationRequestParams = ref<ListResourceRequestParams>({
  page: 1,
  pageSize: 10,
  search: '',
  namespaces: []
});
const applicationRequestOptions = ref<ResourceQueryOptions>({
  enabled: true,
  polling: false,
});
const {data: applications, isLoading: isLoadingApplications, isError: isErrorApplications, error: applicationsError} = useApplications(store, applicationRequestParams, applicationRequestOptions);


const {mutateAsync: createService, isPending: isCreatingService, isError: createServiceError, error: createServiceErrorData} = useCreateServiceInstance(store, () => {
  handleSuccess('create');
  closeModal();
});
const {mutateAsync: bindService, isError: bindServiceError} = useBindServiceInstance(store);
const {mutateAsync: unbindService, isError: unbindServiceError} = useUnbindServiceInstance(store);
const {mutateAsync: updateService, isPending: isUpdatingService, isError: updateServiceError, error: updateServiceErrorData} = useUpdateServiceInstance(store, () => {
  handleSuccess('update');
  closeModal();
});

// Captured separately so background list polls (which omit internal_routes) can't wipe it
const internalRoutes = ref<string[]>([]);

// Watch for changes to the active namespace cache and update the request params accordingly
watchEffect(() => {
  void store.state.activeNamespaceCacheKey;
  const activeNamespaces = store.state.activeNamespaceCache;

  if (activeNamespaces && Object.keys(activeNamespaces).length > 0) {
    namespaceRequestParams.value.namespaces = Object.keys(activeNamespaces);
  } else {
    namespaceRequestParams.value.namespaces = undefined;
  }
});

const namespaceOpts = computed(() => {
  return namespaces?.value?.items.map((ns) => ({ label: ns.meta.name, value: ns.meta.name })) || [];
});

const catalogServiceOpts = computed(() => {
  return catalogServices?.value?.items.map((cs) => ({
    label: `${cs.meta.name} (${cs.shortDescription})`,
    value: cs.meta.name,
  })) || [];
});

const nsAppOptions = computed(() => {
  if (!formNamespace.value) return [];

  return applications?.value?.items
    .map((a: any) => ({ label: a.meta.name, value: a.meta.name })) || [];
});

const selectedCatalogService = computed(() =>
  catalogServices?.value?.items.find((cs) => cs.meta.name === formCatalogService.value)
);

const showChartValues = computed(() =>
  Object.keys(selectedCatalogService.value?.settings || {}).length !== 0 || (isEdit.value && Object.keys(chartValues).length !== 0)
);

const isEdit = computed(() => modalMode.value === 'edit');
const isView = computed(() => modalMode.value === 'view');

const isDirty = computed(() => {
  if (isView.value) return false;

  if (isEdit.value) {
    if (!serviceModel.value) return false;

    const settingsChanged = !isEqual(
      objValuesToString(chartValues),
      objValuesToString(serviceModel.value.settings || {})
    );
    const appsChanged = !isEqual(
      [...selectedApps.value].sort(),
      [...initialBoundApps.value].sort()
    );

    return settingsChanged || appsChanged;
  }

  return !!(formName.value || formCatalogService.value || selectedApps.value.length || Object.keys(chartValues).length);
});

const showDiscardConfirm = ref(false);

const validationPassed = computed(() => {
  if (isEdit.value) {
    if (!serviceModel.value) return false;

    const newSettings = !isEqual(
      objValuesToString(chartValues),
      objValuesToString(serviceModel.value.settings || {})
    );
    const appBindingChanged = !isEqual(
      [...selectedApps.value].sort(),
      [...initialBoundApps.value].sort()
    );

    return newSettings || appBindingChanged;
  }

  if (!formCatalogService.value) return false;
  if (!formName.value) return false;
  if (!formNamespace.value) return false;
  if (showChartValues.value && !Object.values(validChartValues.value).every((v) => !!v)) return false;

  const nameErrors = validateKubernetesName(formName.value, '', store.getters, undefined, []);
  const nsErrors = validateKubernetesName(formNamespace.value, '', store.getters, undefined, []);

  return nameErrors.length === 0 && nsErrors.length === 0;
});

async function openCreate(prefilledCatalogService?: string) {
  modalMode.value = 'create';

  serviceModel.value = null;
  formNamespace.value = '';
  formName.value = '';
  formCatalogService.value = prefilledCatalogService || '';

  selectedApps.value = [];
  initialBoundApps.value = [];
  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  validChartValues.value = {};

  namespaceRequestOptions.value.enabled = true;
  catalogServiceRequestOptions.value.enabled = true;
  showModal.value = true;
}

function populateForm(row: ServiceInstance) {
  serviceModel.value = row;
  formNamespace.value = row.meta?.namespace || '';
  formName.value = row.meta?.name || '';
  formCatalogService.value = row.catalogService || '';
  internalRoutes.value = [...(row.internalRoutes || [])];

  selectedApps.value = [...(row.boundApps || [])];
  initialBoundApps.value = [...(row.boundApps || [])];

  const settings = objValuesToString(row.settings || {});

  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  Object.assign(chartValues, settings);
  validChartValues.value = {};
}

function openView(row: ServiceInstance) {
  modalMode.value = 'view';

  populateForm(row);
  
  namespaceRequestOptions.value.enabled = true;
  catalogServiceRequestOptions.value.enabled = true;
  showModal.value = true;
}

function openEdit(row: ServiceInstance) {
  modalMode.value = 'edit';

  populateForm(row);

  namespaceRequestOptions.value.enabled = true;
  catalogServiceRequestOptions.value.enabled = true;
  showModal.value = true;
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  // Clear form state before setting showModal = false so that when Lit fires
  // modal-close (which triggers handleModalClose), isDirty is already false
  formName.value = '';
  formCatalogService.value = '';
  formNamespace.value = '';
  selectedApps.value = [];
  initialBoundApps.value = [];
  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  validChartValues.value = {};
  internalRoutes.value = [];
  serviceModel.value = null;
  showDiscardConfirm.value = false;
  namespaceRequestOptions.value.enabled = false;
  catalogServiceRequestOptions.value.enabled = false;
  namespaceRequestParams.value.page = 1;
  namespaceRequestParams.value.search = '';
  showModal.value = false;
}

function resetChartValues() {
  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  validChartValues.value = {};
}

async function onSubmit() {
  if (!validationPassed.value || isCreatingService.value || isUpdatingService.value) return;

  if (!isEdit.value) {
    // Capture values before closeModal() wipes form state
    const capturedNamespace = formNamespace.value;
    const capturedName = formName.value;
    const capturedSelectedApps = [...selectedApps.value];

    const cleanSettings = { ...chartValues };

    delete cleanSettings.value;
    const request = {
      name: capturedName,
      catalogService: formCatalogService.value,
      settings: cleanSettings,
      wait: capturedSelectedApps.length > 0,
    };

    await createService({ namespace: capturedNamespace, request });

    if (capturedSelectedApps.length) {
      Promise.all(capturedSelectedApps.map((app: string) => bindService({ namespace: capturedNamespace, serviceName: capturedName, request: { appName: app } })))
    }
  } else {
    const svc = {...serviceModel.value};
    if (!svc) throw new Error('Service model is missing');
    const newSettings = !isEqual(
      objValuesToString(chartValues),
      objValuesToString(svc.settings || {})
    );

    if (newSettings) {
      const cleanSettings = { ...chartValues };

      delete cleanSettings.value;
      const request = {
        settings: cleanSettings,
        wait: selectedApps.value.length > 0,
      };
      await updateService({ namespace: svc.meta?.namespace || '', serviceName: svc.meta?.name || '', request });
    }

    const bindApps = selectedApps.value;
    const unbindApps = initialBoundApps.value.filter(a => !bindApps.includes(a));
    const newBindApps = bindApps.filter(a => !initialBoundApps.value.includes(a));
    const serviceName = svc.meta?.name;

    if (showModal.value) {
      closeModal();
    }

    // Bind/unbind and refresh in the background
    if (newBindApps.length > 0 || unbindApps.length > 0) {
      Promise.all([
        ...newBindApps.map((a: string) => bindService({ namespace: svc.meta?.namespace || '', serviceName: serviceName || '', request: { appName: a } })),
        ...unbindApps.map((a: string) => unbindService({ namespace: svc.meta?.namespace || '', serviceName: serviceName || '', request: { appName: a } })),
      ]).then(() => {
        store.dispatch('growl/success', {
          title:   t(`epinio.growl.service.both.success.title`),
          message: t(`epinio.growl.service.both.success.message`, { name: svc.meta?.name }),
        });
      })
    }
    
  }
}

watchEffect(() => {
  if (bindServiceError.value) {
    store.dispatch('growl/error', {
      title: t('epinio.growl.serviceInstance.bind.error.title'),
      message: t('epinio.growl.serviceInstance.bind.error.message'),
    });
  }
  if (unbindServiceError.value) {
    store.dispatch('growl/error', {
      title: t('epinio.growl.serviceInstance.unbind.error.title'),
      message: t('epinio.growl.serviceInstance.unbind.error.message'),
    });
  }
});

const handleSuccess = (type: 'create' | 'update') => {
  store.dispatch('growl/success', {
    title:   t(`epinio.growl.serviceInstance.${type}.success.title`),
    message: t(`epinio.growl.serviceInstance.${type}.success.message`, { name: formName.value }),
  });
};

const onNamespaceFilter = debounce((query: string) => {
  namespaceRequestParams.value.page = 1;
  namespaceRequestParams.value.search = query;
}, 500);

const onCatalogServiceFilter = debounce((query: string) => {
  catalogServiceRequestParams.value.page = 1;
  catalogServiceRequestParams.value.search = query;
}, 500);

const onApplicationFilter = debounce((query: string) => {
  applicationRequestParams.value.page = 1;
  applicationRequestParams.value.search = query;
}, 500);

// watch namespace changes to fetch applications for the selected namespace
watch(formNamespace, (newNamespace) => {
  if (newNamespace) {
    applicationRequestParams.value.page = 1;
    applicationRequestParams.value.search = '';
    applicationRequestParams.value.namespaces = [newNamespace];
  }
}, { immediate: true });

__expose({ openCreate, openEdit, openView });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    ".dismissible": false,
    title: (isEdit.value || isView.value) ? formName.value : 'Instances',
    subtitle: (isEdit.value || isView.value) ? (serviceModel.value?.stateDisplay || '') : 'Create New',
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-form-card", null, [
        _createElementVNode("trailhand-form-row", _hoisted_3, [
          _createElementVNode("trailhand-dropdown", {
            style: {"width":"100%"},
            options: namespaceOpts.value,
            value: formNamespace.value,
            label: "Namespace",
            placeholder: "Select a namespace",
            disabled: isEdit.value || isView.value,
            required: !isView.value,
            filterable: "",
            isLoading: _unref(isLoadingNamespaces),
            onDropdownChange: _cache[0] || (_cache[0] = (e) => { formNamespace.value = e.detail.value; selectedApps.value = []; }),
            onDropdownFilter: _cache[1] || (_cache[1] = (e) => { _unref(onNamespaceFilter)(e.detail.filter); })
          }, null, 40, _hoisted_4),
          _cache[10] || (_cache[10] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: formName.value,
            label: "Name",
            placeholder: "A Unique Name",
            required: true,
            disabled: isEdit.value || isView.value,
            onTextInputChange: _cache[2] || (_cache[2] = (e) => { formName.value = e.detail.value; })
          }, null, 40, _hoisted_5)
        ]),
        _cache[12] || (_cache[12] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", {
          columns: (isView.value || isEdit.value) ? '4' : '1'
        }, [
          _createElementVNode("trailhand-dropdown", {
            style: {"width":"100%"},
            options: catalogServiceOpts.value,
            value: formCatalogService.value,
            label: "Catalog Service",
            placeholder: "Select the type of service to create",
            disabled: isEdit.value || isView.value,
            required: !isView.value,
            filterable: "",
            isLoading: _unref(isLoadingCatalogServices),
            onDropdownChange: _cache[3] || (_cache[3] = (e) => { formCatalogService.value = e.detail.value; resetChartValues(); }),
            onDropdownFilter: _cache[4] || (_cache[4] = (e) => { _unref(onCatalogServiceFilter)(e.detail.filter); })
          }, null, 40, _hoisted_7),
          _cache[11] || (_cache[11] = _createTextVNode()),
          (isView.value || isEdit.value)
            ? (_openBlock(), _createElementBlock("trailhand-text-input", {
                key: 0,
                value: serviceModel.value?.catalogServiceVersion || '',
                label: "Cat. Service Version",
                disabled: true
              }, null, 8, _hoisted_8))
            : _createCommentVNode("", true)
        ], 8, _hoisted_6),
        _cache[13] || (_cache[13] = _createTextVNode()),
        ((isView.value || isEdit.value) && internalRoutes.value.length)
          ? (_openBlock(), _createElementBlock("trailhand-form-row", _hoisted_9, [
              _createElementVNode("trailhand-code-editor", {
                style: {"width":"100%"},
                value: internalRoutes.value.join('\n'),
                label: "Internal Routes",
                disabled: true
              }, null, 8, _hoisted_10)
            ]))
          : _createCommentVNode("", true),
        _cache[14] || (_cache[14] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", null, [
          _createElementVNode("trailhand-dropdown", {
            values: selectedApps.value,
            options: nsAppOptions.value,
            label: "Bind to Application (Optional)",
            disabled: isView.value || !formNamespace.value || _unref(isLoadingApplications),
            filterable: "",
            multiselect: "",
            placeholder: "Select applications to bind",
            onDropdownChange: _cache[5] || (_cache[5] = (e) => { selectedApps.value = e.detail.values; }),
            onDropdownFilter: _cache[6] || (_cache[6] = (e) => { _unref(onApplicationFilter)(e.detail.filter); })
          }, null, 40, _hoisted_11)
        ]),
        _cache[15] || (_cache[15] = _createTextVNode()),
        (showChartValues.value)
          ? (_openBlock(), _createElementBlock("trailhand-form-row", {
              key: 1,
              title: _unref(t)('epinio.services.chartValues.title')
            }, [
              _createVNode(ChartValues, {
                value: chartValues,
                "onUpdate:value": _cache[7] || (_cache[7] = ($event: any) => ((chartValues) = $event)),
                chart: selectedCatalogService.value?.settings ?? [],
                title: _unref(t)('epinio.services.chartValues.title'),
                mode: isEdit.value ? 'edit' : 'create',
                disabled: isView.value,
                onValid: _cache[8] || (_cache[8] = ($event: any) => (validChartValues.value = $event))
              }, null, 8, ["value", "chart", "title", "mode", "disabled"])
            ], 8, _hoisted_12))
          : _createCommentVNode("", true)
      ]),
      _cache[16] || (_cache[16] = _createTextVNode()),
      (_unref(createServiceError) || _unref(updateServiceError))
        ? (_openBlock(), _createBlock(Banner, {
            key: 0,
            color: "error",
            label: _unref(createServiceErrorData)?.message || _unref(updateServiceErrorData)?.message || _unref(t)('epinio.services.errors.save')
          }, null, 8, ["label"]))
        : _createCommentVNode("", true),
      _cache[17] || (_cache[17] = _createTextVNode()),
      (_unref(isErrorNamespaces) || _unref(isErrorCatalogServices) || _unref(isErrorApplications))
        ? (_openBlock(), _createBlock(Banner, {
            key: 1,
            color: "error",
            label: _unref(namespacesError)?.message || _unref(catalogServicesError)?.message || _unref(applicationsError)?.message || _unref(t)('epinio.services.errors.optionsFetch')
          }, null, 8, ["label"]))
        : _createCommentVNode("", true)
    ]),
    _cache[23] || (_cache[23] = _createTextVNode()),
    _createElementVNode("div", _hoisted_13, [
      (isView.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: closeModal
            }, "\n          Close\n        ", 32),
            _cache[18] || (_cache[18] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "primary",
              onButtonClick: _cache[9] || (_cache[9] = ($event: any) => (modalMode.value = 'edit'))
            }, "\n          Edit Configuration\n        ", 32)
          ], 64))
        : (showDiscardConfirm.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[19] || (_cache[19] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
              _cache[20] || (_cache[20] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleKeepEditing
              }, "\n          Keep Editing\n        ", 32),
              _cache[21] || (_cache[21] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "destructive",
                onButtonClick: handleDiscard
              }, "\n          Discard\n        ", 32)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleModalClose
              }, "\n          Cancel\n        ", 32),
              _cache[22] || (_cache[22] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "primary",
                disabled: !validationPassed.value || _unref(isCreatingService) || _unref(isUpdatingService),
                onButtonClick: onSubmit
              }, _toDisplayString(isEdit.value ? (_unref(isUpdatingService) ? _unref(t)('generic.updating') : _unref(t)('generic.save')) : (_unref(isCreatingService) ? _unref(t)('generic.creating') : _unref(t)('generic.create'))), 41, _hoisted_14)
            ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})